import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getOrderStatus } from '../../services/shopService';
import { renderProductImage } from '../../utils/imageHelpers';

const OrderDetail = () => {
  const { shopId, orderId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadOrder = async () => {
      if (!currentUser) return;
      try {
        const orderData = await getOrderStatus(shopId, orderId);
        if (!orderData) {
          setError('Ordine non trovato');
          return;
        }
        setOrder({ id: orderId, ...orderData });
      } catch (err) {
        console.error('Errore nel caricamento ordine:', err);
        setError('Errore nel caricamento dell\'ordine');
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
  }, [shopId, orderId, currentUser]);

  const getStatusBadge = (status) => {
    const config = {
      'pending_confirmation': { variant: 'warning', text: 'In attesa di conferma' },
      'confirmed': { variant: 'success', text: 'Confermato' },
      'processing': { variant: 'info', text: 'In elaborazione' },
      'shipped': { variant: 'primary', text: 'Spedito' },
      'delivered': { variant: 'success', text: 'Consegnato' },
      'cancelled': { variant: 'danger', text: 'Annullato' }
    };
    const c = config[status] || { variant: 'secondary', text: status };
    return <Badge bg={c.variant}>{c.text}</Badge>;
  };

  const getPaymentBadge = (paymentStatus, paymentMethod) => {
    if (!paymentMethod) return null;
    return (
      <div className="mb-3 p-3 rounded-3" style={{ backgroundColor: paymentStatus === 'paid' ? '#D4EDDA' : '#FFF3CD' }}>
        <h6 className="fw-bold mb-1">
          <i className="bi bi-credit-card me-2"></i>
          Pagamento: {paymentMethod}
        </h6>
        <Badge bg={paymentStatus === 'paid' ? 'success' : 'warning'}>
          {paymentStatus === 'paid' ? 'Pagamento verificato' : 'In attesa di verifica'}
        </Badge>
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="text-center py-5">
        <p className="text-danger mb-3">{error || 'Ordine non trovato'}</p>
        <Button variant="primary" className="rounded-pill" onClick={() => navigate('/profile/orders')}>
          Torna agli ordini
        </Button>
      </div>
    );
  }

  return (
    <div className="py-2">
      <div className="mb-3 d-flex justify-content-start">
        <Button
          variant="dark"
          className="rounded-circle shadow-sm border-0"
          style={{ width: 44, height: 44, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => navigate('/profile/orders')}
        >
          <i className="bi bi-arrow-left fs-4 text-white"></i>
        </Button>
      </div>

      <h4 className="mb-4 fw-bold">Dettaglio Ordine</h4>

      {/* Info Ordine */}
      <Card className="mb-4 rounded-4 shop-card">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h5 className="fw-bold mb-1">Ordine #{String(order.orderNumber).padStart(6, '0')}</h5>
              <p className="text-muted small mb-0">{formatDate(order.createdAt)}</p>
            </div>
            {getStatusBadge(order.status)}
          </div>

          {/* Stato Pagamento */}
          {getPaymentBadge(order.paymentStatus, order.paymentMethod)}

          <div className="d-flex justify-content-between align-items-center">
            <div>
              <p className="text-muted small mb-0">Totale</p>
              <p className="mb-0 fw-bold fs-5">€{order.total.toFixed(2)}</p>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Prodotti */}
      <h5 className="mb-3 fw-bold">Prodotti</h5>
      <Card className="mb-4 rounded-4 shop-card">
        <Card.Body>
          {order.items?.map((item, index) => (
            <React.Fragment key={item.id || index}>
              {index > 0 && <hr className="my-3" />}
              <Row className="align-items-center">
                <Col xs={3}>
                  <div className="rounded-4 overflow-hidden bg-white position-relative" style={{ width: '70px', height: '70px' }}>
                    {renderProductImage(item.image, item.name)}
                    {item.isAvailable === false && (
                      <div
                        className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                          top: 0, left: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          backdropFilter: 'blur(2px)'
                        }}
                      >
                        <i className="bi bi-slash-circle text-white" style={{ fontSize: '1.5rem' }}></i>
                      </div>
                    )}
                  </div>
                </Col>
                <Col xs={6}>
                  <p className="mb-0 fw-medium text-dark" style={{ opacity: item.isAvailable === false ? 0.5 : 1 }}>
                    {item.name}
                  </p>
                  <p className="text-primary mb-0 fw-bold" style={{ opacity: item.isAvailable === false ? 0.5 : 1 }}>
                    €{item.price.toFixed(2)}
                  </p>
                  <small className="text-muted">Qtà: {item.quantity}</small>
                  {item.isAvailable === false && (
                    <Badge bg="danger" className="ms-2" style={{ fontSize: '0.7rem' }}>Non disponibile</Badge>
                  )}
                </Col>
                <Col xs={3} className="text-end">
                  <p className="fw-bold mb-0 text-dark" style={{ opacity: item.isAvailable === false ? 0.5 : 1 }}>
                    €{(item.price * item.quantity).toFixed(2)}
                  </p>
                </Col>
              </Row>
            </React.Fragment>
          ))}
        </Card.Body>
      </Card>

      {/* Riepilogo */}
      <Card className="mb-4 rounded-4 shop-card">
        <Card.Body>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-secondary">Subtotale</span>
            <span className="text-dark">€{(order.subtotal || 0).toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-secondary">Spedizione</span>
            <span className="text-dark">
              {order.shippingCost === 0 ? (
                <span className="text-success fw-medium">Gratuita</span>
              ) : (
                `€${(order.shippingCost || 0).toFixed(2)}`
              )}
            </span>
          </div>
          <hr />
          <div className="d-flex justify-content-between">
            <span className="fw-bold text-dark">Totale</span>
            <span className="fw-bold text-dark">€{order.total.toFixed(2)}</span>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default OrderDetail;
